/**
 * Castle (castle.io) request-token integration.
 *
 * Mirrors the lifecycle of `initAgent`/`getAgent` for the FingerprintJS Pro
 * SDK in `utils/fingerprintProAgent.ts`:
 *
 * - `initCastle({ publishableKey })` is fire-and-forget at app bootstrap. It
 *   kicks off the dynamic import of `@castleio/castle-js` and the call to
 *   `Castle.configure({ pk })`, caching the in-flight promise on the
 *   module-level `castleInitPromise` so callers don't re-trigger it.
 * - `getCastleToken({ timeoutMs? })` awaits the cached init promise so it can
 *   never race against `configure()`, then calls `createRequestToken()` with
 *   a default 500 ms timeout (matching the prior identity-navigation
 *   behaviour) so a slow Castle response never blocks an authenticated
 *   request such as sign-out.
 * - `setCastleToken(handler)` is the fire-and-forget variant preserved for
 *   unified-login's fetch middleware.
 *
 * The Castle SDK (~600 KB) is loaded lazily via dynamic `import()` so it
 * doesn't affect the initial bundle of host apps that never sign the user in
 * or out. All `import('@castleio/castle-js')` calls go through
 * `dynamicImportCastle` which dedupes them to a single module-cache entry.
 */
import { ActionType, ComponentType, logEvent } from '@cbhq/client-analytics';

import { PACKAGE_VERSION } from '../version';

import {
  configureCastle,
  createCastleRequestToken,
  resetCastleModuleForTests,
} from './dynamicImportCastle';

const DEFAULT_CASTLE_TOKEN_TIMEOUT_MS = 500;

let castlePublishableKey: string | undefined;
let castleInitPromise: Promise<void> | undefined;

export type InitCastleOptions = {
  /**
   * Castle publishable key. When omitted (or empty), Castle will not be
   * initialised and `getCastleToken`/`setCastleToken` become no-ops.
   */
  publishableKey?: string;
};

export type GetCastleTokenOptions = {
  /**
   * Maximum time, in milliseconds, to wait for Castle to return a token
   * before resolving with an empty string. Pass `0` to wait indefinitely.
   * Defaults to 500 ms.
   */
  timeoutMs?: number;
};

function nowMs(): number {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}

/**
 * Kick off Castle SDK initialisation. Synchronous (returns void) — the
 * caller does not need to await; subsequent `getCastleToken()` calls will
 * await the cached promise internally.
 *
 * Three paths, modelled on `initAgent`:
 *  1. Already initialised with the same key → no-op
 *  2. Missing/empty publishable key → no-op (no warning — this is a valid
 *     "Castle not used by this host" configuration)
 *  3. First call (or key change) → kicks off configure() and caches the
 *     in-flight promise so token callers can `await` it
 */
export function initCastle({ publishableKey }: InitCastleOptions = {}): void {
  if (!publishableKey) {
    return;
  }

  if (castleInitPromise && castlePublishableKey === publishableKey) {
    return;
  }

  castlePublishableKey = publishableKey;

  logEvent('identity_fingerprint_castle_init_started', {
    action: ActionType.api,
    componentType: ComponentType.api,
    version: PACKAGE_VERSION,
  });

  const startedAt = nowMs();

  castleInitPromise = configureCastle(publishableKey)
    .then(() => {
      logEvent('identity_fingerprint_castle_init_completed', {
        action: ActionType.api,
        componentType: ComponentType.api,
        perf_castle_init_ms: Math.round(nowMs() - startedAt),
        version: PACKAGE_VERSION,
      });
    })
    .catch((error: unknown) => {
      logEvent('identity_fingerprint_castle_init_failed', {
        action: ActionType.api,
        componentType: ComponentType.api,
        error_name: error instanceof Error ? error.name : 'unknown',
        version: PACKAGE_VERSION,
      });
      // Reset so a future initCastle call can retry rather than serving the
      // failed promise forever.
      castleInitPromise = undefined;
      castlePublishableKey = undefined;
    });
}

/**
 * Reset internal Castle state. Intended for use in tests only.
 * @internal
 */
export function resetCastleForTests(): void {
  castlePublishableKey = undefined;
  castleInitPromise = undefined;
  resetCastleModuleForTests();
}

/**
 * Returns a Castle request token for the current call.
 *
 * - Resolves with `''` if Castle has not been initialised via
 *   `initCastle({ publishableKey })`. The SDK is treated as optional —
 *   callers that don't configure it get the empty-string contract,
 *   matching the Arkose/Sardine pattern. No warning is emitted.
 * - Resolves with `''` if the SDK throws or times out.
 * - Awaits the in-flight `initCastle()` promise so callers don't race
 *   against `configure()` at bootstrap.
 *
 * The default 500 ms timeout matches the prior identity-navigation
 * behaviour and prevents slow Castle responses from blocking authenticated
 * requests (sign-out, verify-identification).
 */
export async function getCastleToken({
  timeoutMs = DEFAULT_CASTLE_TOKEN_TIMEOUT_MS,
}: GetCastleTokenOptions = {}): Promise<string> {
  if (!castleInitPromise) {
    return '';
  }

  await castleInitPromise;

  const tokenPromise = createCastleRequestToken().catch((error: unknown) => {
    logEvent('identity_fingerprint_castle_token_failed', {
      action: ActionType.api,
      componentType: ComponentType.api,
      error_name: error instanceof Error ? error.name : 'unknown',
      version: PACKAGE_VERSION,
    });
    return '';
  });

  if (!timeoutMs || timeoutMs <= 0) {
    return tokenPromise;
  }

  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timeoutPromise = new Promise<string>((resolve) => {
    timeoutId = setTimeout(() => {
      logEvent('identity_fingerprint_castle_token_timed_out', {
        action: ActionType.api,
        componentType: ComponentType.api,
        timeout_ms: timeoutMs,
        version: PACKAGE_VERSION,
      });
      resolve('');
    }, timeoutMs);
  });

  return Promise.race([tokenPromise, timeoutPromise]).finally(() => {
    if (timeoutId) clearTimeout(timeoutId);
  });
}

/**
 * Fire-and-forget variant: calls `handler` with a Castle request token once
 * it's available. The handler is NOT invoked when Castle has not been
 * initialised, when the SDK fails to load, or when it throws / returns an
 * empty token.
 *
 * Preserves the historical `setCastleToken(handler)` API used by
 * unified-login's fetch middleware.
 */
export function setCastleToken(handler: (token: string) => void): void {
  if (!castleInitPromise) {
    return;
  }

  void (async () => {
    await castleInitPromise;
    const token = await createCastleRequestToken().catch((error: unknown) => {
      logEvent('identity_fingerprint_castle_token_failed', {
        action: ActionType.api,
        componentType: ComponentType.api,
        error_name: error instanceof Error ? error.name : 'unknown',
        version: PACKAGE_VERSION,
      });
      return '';
    });
    if (token) {
      handler(token);
    }
  })();
}
